import { useEffect, useState } from "react";

const SUNSET_STAGES = [
  { sun: 168, top: "#f6d7a8", mid: "#f3b183", low: "#7eb7c9", glow: "#ffe7b8" },
  { sun: 198, top: "#f6d2a0", mid: "#f1aa78", low: "#8bb8c4", glow: "#ffe1ac" },
  { sun: 228, top: "#f6c896", mid: "#ef9b6c", low: "#98b0b8", glow: "#ffd89c" },
  { sun: 258, top: "#f6be88", mid: "#ed8c5e", low: "#b49a90", glow: "#ffcf8c" },
  { sun: 290, top: "#f5b278", mid: "#eb7c50", low: "#c88a72", glow: "#ffc47c" },
  { sun: 322, top: "#f4a468", mid: "#e86e44", low: "#d67c58", glow: "#ffb86c" },
  { sun: 356, top: "#f29658", mid: "#e56038", low: "#de6e44", glow: "#ffac5c" },
  { sun: 390, top: "#ef8848", mid: "#e05430", low: "#d06238", glow: "#ffa04e" },
  { sun: 424, top: "#ec7c3c", mid: "#d84a28", low: "#c45830", glow: "#ff9440" },
  { sun: 458, top: "#e87234", mid: "#d04222", low: "#b65028", glow: "#ff8834" },
  { sun: 490, top: "#e4682e", mid: "#c83e1e", low: "#a84822", glow: "#ff7c2a" },
  { sun: 524, top: "#e08a2a", mid: "#c46f16", low: "#9a4218", glow: "#ffb15a" },
];

const RISE_MINUTES = 6 * 60 + 30;
const SET_MINUTES = 18 * 60 + 45;

function wavePath(base, amp, span) {
  const end = 1600 + span;
  let d = `M0 ${base}`;
  for (let x = 0; x < end; x += span) {
    const mid = x + span / 2;
    const next = x + span;
    d += ` C${x + span * 0.28} ${base - amp} ${mid - span * 0.08} ${base - amp} ${mid} ${base}`;
    d += ` C${mid + span * 0.22} ${base + amp} ${next - span * 0.12} ${base + amp * 0.15} ${next} ${base}`;
  }
  return `${d} L${end} 900 L0 900 Z`;
}

const BACK_SPAN = 560;
const FRONT_SPAN = 420;
const BACK_WAVE = wavePath(545, 38, BACK_SPAN);
const FRONT_WAVE = wavePath(650, 26, FRONT_SPAN);

function hexToRgb(hex) {
  const value = hex.replace("#", "");
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
}

function mixHex(from, to, amount) {
  const start = hexToRgb(from);
  const end = hexToRgb(to);
  const channel = (index) => Math.round(start[index] + (end[index] - start[index]) * amount);
  return `rgb(${channel(0)}, ${channel(1)}, ${channel(2)})`;
}

function mixLook(stage) {
  const last = SUNSET_STAGES.length - 1;
  const clamped = Math.min(last, Math.max(0, stage));
  const index = Math.min(last - 1, Math.floor(clamped));
  const amount = clamped >= last ? 1 : clamped - index;
  const from = SUNSET_STAGES[clamped >= last ? last - 1 : index];
  const to = SUNSET_STAGES[clamped >= last ? last : index + 1];
  return {
    sun: from.sun + (to.sun - from.sun) * amount,
    top: mixHex(from.top, to.top, amount),
    mid: mixHex(from.mid, to.mid, amount),
    low: mixHex(from.low, to.low, amount),
    glow: mixHex(from.glow, to.glow, amount),
  };
}

function minutesOf(date) {
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
}

export function isNight(date) {
  const minutes = minutesOf(date);
  return minutes < RISE_MINUTES || minutes >= SET_MINUTES;
}

export function sunsetStage(date) {
  const minutes = minutesOf(date);
  if (isNight(date)) {
    return SUNSET_STAGES.length - 1;
  }
  const across = (minutes - RISE_MINUTES) / (SET_MINUTES - RISE_MINUTES);
  const altitude = Math.sin(Math.PI * across);
  return (SUNSET_STAGES.length - 1) * (1 - altitude);
}

function starField(count) {
  let seed = 19;
  const next = () => {
    seed = (seed * 48271) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  return Array.from({ length: count }, (_, id) => ({
    id,
    cx: 30 + next() * 1540,
    cy: 18 + next() * 460,
    r: next() > 0.88 ? 2.6 : 1 + next() * 1.1,
    delay: `${(next() * 4.5).toFixed(2)}s`,
    duration: `${(2.6 + next() * 3.2).toFixed(2)}s`,
  }));
}

const STARS = starField(56);

function nextShotWait() {
  const fiveMinutes = 5 * 60 * 1000;
  const jitter = (Math.random() - 0.5) * 60 * 1000;
  return fiveMinutes + jitter;
}

function randomShot() {
  const drift = 220 + Math.random() * 280;
  const drop = 36 + Math.random() * 110;
  return {
    id: Date.now(),
    x: 60 + Math.random() * 980,
    y: 30 + Math.random() * 220,
    drift,
    drop,
  };
}

function ShootingStar({ shot }) {
  const length = Math.hypot(shot.drift, shot.drop) || 1;
  const tail = 72;
  return (
    <g>
      <animateTransform
        attributeName="transform"
        type="translate"
        from={`${shot.x} ${shot.y}`}
        to={`${shot.x + shot.drift} ${shot.y + shot.drop}`}
        dur="1.6s"
        fill="freeze"
      />
      <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.62;1" dur="1.6s" fill="freeze" />
      <line
        x1="0"
        y1="0"
        x2={(-shot.drift / length) * tail}
        y2={(-shot.drop / length) * tail}
        stroke="#fffaf4"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="0" cy="0" r="2.6" fill="#fffaf4" />
    </g>
  );
}

function SunsetHero() {
  const [look, setLook] = useState(() => mixLook(sunsetStage(new Date())));
  const [night, setNight] = useState(() => isNight(new Date()));
  const [still, setStill] = useState(false);
  const [shot, setShot] = useState(null);

  useEffect(() => {
    const paint = () => {
      const now = new Date();
      setNight(isNight(now));
      setLook(mixLook(sunsetStage(now)));
    };
    paint();
    const timer = window.setInterval(paint, 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setStill(media.matches);
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!night || still) {
      return undefined;
    }
    let cancelled = false;
    let timer = 0;
    const schedule = (wait) => {
      timer = window.setTimeout(() => {
        if (cancelled) {
          return;
        }
        setShot(randomShot());
        timer = window.setTimeout(() => {
          if (cancelled) {
            return;
          }
          setShot(null);
          schedule(nextShotWait());
        }, 1700);
      }, wait);
    };
    schedule(nextShotWait());
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [night, still]);

  return (
    <div className="sunset-hero" aria-hidden="true">
      <svg viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="sunset-sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={look.top} />
            <stop offset="0.48" stopColor={look.mid} />
            <stop offset="1" stopColor={look.low} />
          </linearGradient>
          <radialGradient id="night-sky" cx="70%" cy="18%" r="85%">
            <stop offset="0" stopColor="#1a3d55" />
            <stop offset="0.45" stopColor="#102536" />
            <stop offset="1" stopColor="#071018" />
          </radialGradient>
        </defs>
        <rect width="1600" height="900" fill={night ? "url(#night-sky)" : "url(#sunset-sky)"} />
        {night ? (
          <>
            <circle cx="1180" cy="148" r="92" fill="#f6efe4" opacity="0.18" />
            <circle cx="1180" cy="148" r="46" fill="#fffaf4" />
            {STARS.map((star) => (
              <circle
                key={star.id}
                className={still ? "sunset-star is-still" : "sunset-star"}
                cx={star.cx}
                cy={star.cy}
                r={star.r}
                fill="#fffaf4"
                style={still ? undefined : { animationDelay: star.delay, animationDuration: star.duration }}
              />
            ))}
            {shot ? <ShootingStar shot={shot} /> : null}
          </>
        ) : (
          <circle cx="1240" cy={look.sun} r="78" fill={look.glow} />
        )}
        <g>
          {still ? null : (
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0 0"
              to={`${-BACK_SPAN} 0`}
              dur="110s"
              repeatCount="indefinite"
            />
          )}
          <path d={BACK_WAVE} fill="#2f7f92" />
        </g>
        <g>
          {still ? null : (
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0 0"
              to={`${-FRONT_SPAN} 0`}
              dur="27s"
              repeatCount="indefinite"
            />
          )}
          <path d={FRONT_WAVE} fill="#1f6578" />
        </g>
        <path
          d="M0 760 C300 720 520 800 860 760 C1160 724 1360 790 1600 750 L1600 900 L0 900 Z"
          fill="#e7d3ad"
        />
        <rect x="180" y="690" width="280" height="70" rx="18" fill="#1c3d4a" />
        <circle cx="250" cy="760" r="28" fill="#24343c" />
        <circle cx="390" cy="760" r="28" fill="#24343c" />
      </svg>
    </div>
  );
}

export default SunsetHero;
